import React from 'react';
import {
  FaAppleAlt,
  FaBrain,
  FaBriefcaseMedical,
  FaCashRegister,
  FaChartLine,
  FaComments,
  FaFileInvoiceDollar,
  FaHeartbeat,
  FaHospitalAlt,
  FaNotesMedical,
  FaStethoscope,
  FaSyringe,
  FaTooth,
  FaUserShield,
  FaUserMd,
  FaWalking,
  FaWaveSquare,
} from 'react-icons/fa';
import ProductPageTemplate from '../../components/productPageTemplate';

const data = {
  theme: {
    accent: '#00BEF0',
    accentSoft: '#57D8FF',
    darkBg: '#123A45',
    darkBgSoft: '#1D6F84',
  },
  hero: {
    eyebrows: ['Clínicas e consultórios', 'Agenda, prontuário e caixa', 'NFSe e gestão em uma base'],
    accent: 'Clínicas',
    title: 'que organiza atendimento, prontuário e gestão diária.',
    lead:
      'O ECOSIS Clínicas foi pensado para profissionais e empresas da saúde que precisam unir agenda, consultas, financeiro, cadastros e leitura operacional.',
    support:
      'A solução atende diferentes especialidades e preserva uma rotina mais discreta, organizada e coerente com o fluxo de atendimento em saúde.',
    metrics: [
      'NFSe integrada à operação clínica',
      'Agenda, consultas e prontuário na mesma base',
      'Atende profissional liberal e estruturas maiores',
    ],
    primaryCta: { label: 'Ver módulos', href: '#modulos' },
    secondaryCta: { label: 'Falar com a equipe', href: '#contato-produto' },
    visualCards: [
      { label: 'Atendimento clínico', value: 'Mais fluido' },
      { label: 'Gestão e consultas', value: 'Mesmo sistema' },
    ],
    panelTitle: 'Painel da clínica',
    panelItems: [
      {
        label: 'Agenda',
        title: 'Organização da rotina assistencial',
        meta: 'Agendamentos, consultas e leitura do atendimento concentrados em um fluxo mais estável.',
      },
      {
        label: 'Prontuário',
        title: 'Histórico clínico acessível',
        meta: 'Prontuário eletrônico, odontograma e registros ligados ao atendimento cotidiano.',
      },
      {
        label: 'Gestão',
        title: 'Financeiro, caixa e relatórios',
        meta: 'Mais previsibilidade administrativa para profissional liberal e estruturas maiores.',
      },
    ],
  },
  intro: {
    tag: 'Visão geral',
    title: 'Uma aparência mais profissional para um produto voltado a saúde.',
    lead:
      'A página anterior já trazia os blocos principais, mas sem unidade visual. Agora a leitura acompanha melhor o nível de confiança esperado em um sistema clínico.',
    cardTitle: 'Organização clínica sem excesso visual',
    cardText:
      'A estrutura foi pensada para destacar atendimento, prontuário, agenda e gestão com mais clareza, sem transformar a página em uma lista longa e pouco legível.',
    quickPoints: [
      'Agenda, prontuário e financeiro conectados em um mesmo fluxo.',
      'Cobertura para consultórios, clínicas e diferentes perfis de especialidade.',
      'Leitura mais segura e discreta para um contexto sensível como saúde.',
    ],
  },
  modulesSection: {
    tag: 'Módulos principais',
    title: 'Os recursos do ECOSIS Clínicas agora têm estrutura visual consistente com o restante do site.',
    lead:
      'Cada módulo abaixo foi reescrito para manter o conteúdo original, mas com hierarquia mais clara e linguagem mais objetiva.',
  },
  modules: [
    {
      icon: FaFileInvoiceDollar,
      title: 'Documentos fiscais',
      description: 'Emita NFSe dentro do mesmo fluxo da clínica, reduzindo troca de contexto administrativo.',
      bullets: ['NFSe integrada à rotina', 'Menos etapas fora do sistema', 'Gestão mais contínua'],
    },
    {
      icon: FaCashRegister,
      title: 'Financeiro e caixa',
      description: 'Controle a parte administrativa ligada ao atendimento com mais previsibilidade diária.',
      bullets: ['Contas a pagar e receber', 'Controle de caixa', 'Base financeira mais organizada'],
    },
    {
      icon: FaNotesMedical,
      title: 'Agenda inteligente',
      description: 'Acompanhe a rotina de consultas e encaixes com uma visão mais adequada ao atendimento em saúde.',
      bullets: ['Gestão de agenda clínica', 'Apoio ao fluxo de atendimento', 'Menos dispersão operacional'],
    },
    {
      icon: FaStethoscope,
      title: 'Prontuário eletrônico',
      description: 'Concentre o histórico do paciente dentro do sistema para dar continuidade real ao cuidado.',
      bullets: ['Registro clínico centralizado', 'Histórico acessível', 'Mais consistência entre atendimentos'],
    },
    {
      icon: FaHeartbeat,
      title: 'Controle de consultas',
      description: 'Administre atendimentos de forma mais estável, com visão da operação clínica no mesmo ambiente.',
      bullets: ['Acompanhamento das consultas', 'Fluxo clínico mais organizado', 'Base conectada à gestão'],
    },
    {
      icon: FaComments,
      title: 'Discrição operacional',
      description: 'A página anterior já destacava a comunicação discreta entre médicos e secretários como valor importante.',
      bullets: ['Interação mais reservada', 'Rotina mais adequada ao contexto de saúde', 'Menos ruído operacional'],
    },
    {
      icon: FaUserShield,
      title: 'Cadastros e permissões',
      description: 'Controle acessos e cadastros com mais coerência para equipes e especialidades diferentes.',
      bullets: ['Gestão de usuários', 'Permissões de acesso', 'Base mais segura'],
    },
    {
      icon: FaChartLine,
      title: 'Gestão inteligente',
      description: 'Leia a clínica com dashboards, relatórios e suporte ao almoxarifado sem perder foco no atendimento.',
      bullets: ['Dashboards fáceis de interpretar', 'Relatórios gerenciais', 'Relatórios e almoxarifado'],
    },
    {
      icon: FaTooth,
      title: 'Odontograma',
      description: 'Especialidades que exigem recursos específicos também encontram suporte dentro da mesma plataforma.',
      bullets: ['Cobertura para odontologia', 'Registro mais adequado à especialidade', 'Base unificada'],
    },
  ],
  operations: {
    tag: 'Onde encaixa melhor',
    title: 'A solução cobre múltiplos perfis de atendimento em saúde.',
    lead:
      'Esse bloco resume os perfis atendidos e reforça a amplitude do produto sem perder a sensação de organização.',
    audienceTitle: 'Perfis atendidos',
    audienceText:
      'O ECOSIS Clínicas atende desde profissional liberal até estruturas maiores, sempre com foco em agenda, prontuário e gestão integrada.',
    sectors: [
      { icon: FaHospitalAlt, label: 'Clínicas médicas' },
      { icon: FaBriefcaseMedical, label: 'Consultórios' },
      { icon: FaUserMd, label: 'Médicos' },
      { icon: FaTooth, label: 'Dentistas' },
      { icon: FaWalking, label: 'Fisioterapeutas' },
      { icon: FaWaveSquare, label: 'Fonoaudiólogos' },
      { icon: FaAppleAlt, label: 'Nutricionistas' },
      { icon: FaBrain, label: 'Psicólogos' },
      { icon: FaHeartbeat, label: 'Terapeutas ocupacionais' },
      { icon: FaSyringe, label: 'Esteticistas' },
      { icon: FaStethoscope, label: 'Profissionais da saúde' },
    ],
    stat: {
      value: 'Saúde',
      text: 'Atende o profissional liberal e grandes empresas, mantendo a leitura clínica e administrativa na mesma base.',
    },
    cards: [
      {
        title: 'Comunicação clínica',
        text: 'A discrição entre médicos e secretários segue como ponto importante do posicionamento do produto.',
        bullets: ['Fluxo mais reservado', 'Menos ruído no dia a dia da equipe'],
      },
      {
        title: 'Especialidades diferentes',
        text: 'O produto foi posicionado para múltiplos perfis clínicos, inclusive cenários que exigem controle específico como odontologia.',
        bullets: ['Odontograma', 'Cobertura multiprofissional'],
      },
      {
        title: 'Gestão além do atendimento',
        text: 'A estrutura vai além da consulta e suporta relatórios, almoxarifado e organização administrativa.',
        bullets: ['Relatórios operacionais', 'Apoio ao almoxarifado'],
      },
      {
        title: 'Agenda e financeiro conectados',
        text: 'A operação clínica ganha previsibilidade quando a rotina assistencial e a parte administrativa não competem entre si.',
        bullets: ['Agenda inteligente', 'Caixa e contas no mesmo fluxo'],
      },
    ],
  },
  contact: {
    tag: 'Contato',
    title: 'Quer avaliar o ECOSIS Clínicas para sua especialidade ou estrutura?',
    lead: 'A equipe pode analisar seu fluxo de atendimento e apontar a composição mais adequada da implantação.',
  },
};

function EcosisClinicas() {
  return <ProductPageTemplate data={data} />;
}

export default EcosisClinicas;
